/**
 * Copilot Auth Context
 *
 * Manages authentication state for the Sales Copilot mobile app.
 * Handles token storage, auto-refresh, and session persistence.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import api, { tokenService } from "../../services/api";
import type { AuthTokens } from "../../types/auth";

/**
 * Standalone axios instance for auth bootstrap operations.
 *
 * Uses plain axios with NO response interceptors. This prevents the shared
 * api interceptor from intercepting 401s during bootstrap and redirecting to
 * /login (the main app) instead of /copilot/login.
 */
const bootstrapApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

bootstrapApi.interceptors.request.use((config) => {
  const token = tokenService.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── Types ───────────────────────────────────────────────────────────────────

/** User info extracted from login response */
export interface CopilotUser {
  id: string;
  email: string;
  name: string;
  role: string;
  salesperson_id: string | null;
  assigned_accounts: string[];
}

/** Shape exposed by the auth context */
export interface CopilotAuthContextValue {
  user: CopilotUser | null;
  salespersonId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, stayLoggedIn: boolean) => Promise<void>;
  logout: () => void;
}

// ─── Token helpers ───────────────────────────────────────────────────────────

const COPILOT_STAY_LOGGED_IN_KEY = "cm_copilot_stay_logged_in";

/** Decode a JWT payload without verification (for reading expiry). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/** Check if a JWT is expired (with a 30-second buffer). */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1000 < Date.now() + 30_000;
}

/**
 * Was the error an actual server-side auth rejection (4xx), vs. a network
 * failure / 5xx? Only auth rejections should trigger token clearing.
 * Exported for test coverage.
 */
export function isAuthRejection(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
}

/** Extract user info from the login response. */
function userFromResponse(data: {
  id: string;
  email: string;
  name: string;
  role: string;
  salesperson_id?: string | null;
  assigned_accounts?: string[];
}): CopilotUser {
  return {
    id: data.id,
    email: data.email,
    name: data.name,
    role: data.role,
    salesperson_id: data.salesperson_id ?? null,
    assigned_accounts: data.assigned_accounts ?? [],
  };
}

// ─── Context ─────────────────────────────────────────────────────────────────

export const CopilotAuthContext = createContext<CopilotAuthContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function CopilotAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CopilotUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // ── Bootstrap: validate existing session on mount ──────────────────────

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const accessToken = tokenService.getAccessToken();
      const refreshToken = tokenService.getRefreshToken();

      // No tokens at all — user is not logged in.
      if (!accessToken && !refreshToken) {
        setIsLoading(false);
        return;
      }

      // Access token missing but refresh token present: storage was partially
      // evicted (iOS ITP, quota pressure). Try refresh before giving up.
      if (!accessToken) {
        await tryRefresh(cancelled);
        if (!cancelled) setIsLoading(false);
        return;
      }

      // Access token present and still valid — fetch user profile.
      if (!isTokenExpired(accessToken)) {
        try {
          // Use bootstrapApi to avoid the shared api interceptor redirecting
          // to /login on 401 — we handle auth failures ourselves here.
          const { data } = await bootstrapApi.get<{
            id: string;
            email: string;
            name: string;
            role: string;
            salesperson_id: string | null;
            assigned_accounts: string[];
          }>("/auth/me");
          if (!cancelled) setUser(userFromResponse(data));
        } catch (err) {
          // Only treat actual auth rejections (401/403) as invalid. Network
          // errors / 5xx leave the tokens intact — next API call will retry.
          if (isAuthRejection(err)) {
            if (!cancelled) await tryRefresh(cancelled);
          }
        }
      } else {
        // Access token expired, try refresh
        await tryRefresh(cancelled);
      }

      if (!cancelled) setIsLoading(false);
    }

    async function tryRefresh(isCancelled: boolean) {
      const refreshToken = tokenService.getRefreshToken();
      if (!refreshToken || isTokenExpired(refreshToken)) {
        tokenService.clearTokens();
        return;
      }

      try {
        // Use plain axios for refresh — bypasses the shared api interceptor
        // which would redirect to /login (main app) on failure.
        const { data } = await axios.post<{
          access_token: string;
          expires_in: number;
        }>(`${import.meta.env.VITE_API_URL || "/api"}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        // Update only the access token (keep refresh token)
        localStorage.setItem("cm_access_token", data.access_token);
        document.cookie = `cm_auth=${data.access_token}; path=/; max-age=${data.expires_in}; SameSite=Lax`;

        // Now fetch user profile with new token — use bootstrapApi which
        // will pick up the freshly-stored access token via its request interceptor.
        const profileRes = await bootstrapApi.get<{
          id: string;
          email: string;
          name: string;
          role: string;
          salesperson_id: string | null;
          assigned_accounts: string[];
        }>("/auth/me");
        if (!isCancelled) setUser(userFromResponse(profileRes.data));
      } catch (err) {
        // Only clear tokens if the server actually rejected them (4xx).
        // Network errors and 5xx: leave tokens intact so the user isn't
        // force-logged-out on a transient backend blip.
        if (isAuthRejection(err)) {
          tokenService.clearTokens();
        }
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────

  const login = useCallback(async (email: string, password: string, stayLoggedIn: boolean) => {
    const { data } = await api.post<{
      user: {
        id: string;
        email: string;
        name: string;
        role: string;
        salesperson_id: string | null;
        assigned_accounts: string[];
      };
      tokens: AuthTokens;
    }>("/auth/login", { email, password });

    // Store tokens
    if (stayLoggedIn) {
      tokenService.setTokens(data.tokens);
      localStorage.setItem(COPILOT_STAY_LOGGED_IN_KEY, "1");
    } else {
      // Store access token in localStorage + refresh token in sessionStorage.
      // sessionStorage is cleared when the browser tab closes, giving
      // "session-only" persistence. The refresh token is still available
      // during the active session so that silent token refresh works and
      // users don't get spurious "not authenticated" errors when their
      // access token expires mid-use.
      localStorage.setItem("cm_access_token", data.tokens.access_token);
      document.cookie = `cm_auth=${data.tokens.access_token}; path=/; max-age=${data.tokens.expires_in}; SameSite=Lax`;
      tokenService.setSessionRefreshToken(data.tokens.refresh_token);
      localStorage.removeItem(COPILOT_STAY_LOGGED_IN_KEY);
    }

    setUser(userFromResponse(data.user));
  }, []);

  // ── Logout ─────────────────────────────────────────────────────────────

  const logout = useCallback(() => {
    tokenService.clearTokens();
    localStorage.removeItem(COPILOT_STAY_LOGGED_IN_KEY);
    setUser(null);
  }, []);

  // ── Context value ──────────────────────────────────────────────────────

  const value = useMemo<CopilotAuthContextValue>(
    () => ({
      user,
      salespersonId: user?.salesperson_id ?? null,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
    }),
    [user, isLoading, login, logout],
  );

  return (
    <CopilotAuthContext.Provider value={value}>
      {children}
    </CopilotAuthContext.Provider>
  );
}
